import { LevelSim } from "@highmarch/domain";
import { bump, plusFloater, stageSegments, tickNumber, motionOk } from "./kit";
import { flyCoinsTo } from "./fly";
import { NextCardView } from "./nextCard";
import { stageBar, stageShort } from "./runMode";
import { screenTint } from "../render/screenTint";

/**
 * Paces the points pill's reward pulse (the coin's pop and glint and the pill's bump). A kill's reward shows only in
 * the header (owner, 2026-09-24: no gold coin over the body): the number counts up and the "+n" sums every gain,
 * but a pulse per gain would restart the animation every frame of a horde dying at x3, so gains closer than `GapMs`
 * to the pulse in progress join it instead of starting another.
 */
export class RewardPulse {
  static readonly GapMs = 280;
  private last = -Infinity;
  /** A gain arrived at `now` (ms): true when it starts a pulse, false when it joins the one in progress. */
  gain(now: number): boolean {
    if (now - this.last < RewardPulse.GapMs) return false;
    this.last = now;
    return true;
  }
}

/**
 * The HUD's top plate readouts (hud.ts): the power and rerolls line, the points pill and its reward pulse, the next-card
 * pill, the HP bar, the stage segments; and the full-screen hit flash and last-stand vignette. Every write is on a change.
 */
export class HudPills {
  /**
   * The hit flash and the last stand's red edge vignette (level/announcer.ts LevelMoments) are drawn by the post chain's
   * final pass (render/screenTint.ts, M44): they were full-screen DOM layers over the canvas, which cost frames for the
   * rest of a wave once the fortress was low.
   */
  private standOn = false;
  private lastKey = "";
  /** Last values the HUD showed, so a change can tick, bump and flash instead of snapping. NaN = first refresh. */
  private shownPoints = NaN;
  private shownHp = NaN;
  /** The points reward pulse (RewardPulse): the coin pops to `CoinPop` with a tilt of `CoinTilt` deg, the pill to
   * `PillPop`, and a glint star flashes on the coin, all over `PulseMs` (transform and opacity only). */
  static readonly CoinPop = 1.38;
  static readonly CoinTilt = 14;
  static readonly PillPop = 1.1;
  static readonly PulseMs = 440;
  private readonly pulse = new RewardPulse();
  /** Coin bursts this big (a boss) skip the fly throttle. */
  static readonly BigCoinBurst = 6;
  /** Heart pulses (and the bar turns red) at or below this share of base HP. */
  static readonly LowHp = 0.25;

  /** `root` is the HUD's top plate (hud.ts builds its markup); the flash and vignette layers are made here. */
  constructor(private readonly root: HTMLDivElement, private readonly powerScore: number) {
    screenTint.reset();
  }

  /** The fortress took a hit: a quick red wash at the screen edges. Reduced effects: none. */
  hitFlash(): void {
    if (typeof document !== "undefined" && document.body?.classList.contains("reduced-vfx")) return;
    screenTint.hurt(performance.now());
  }

  /**
   * The last stand's vignette: `depth` 0..1 (level/moments.ts lastStandDepth) sets how far the red reaches in from the
   * edges; null fades it out. Cheap to call every frame.
   */
  setLastStand(depth: number | null): void {
    this.standOn = depth !== null;
    screenTint.setLastStand(depth, performance.now());
  }

  /** One heartbeat on the vignette (lub-dub). Reduced effects: none — the vignette stays static. */
  heartbeatPulse(): void {
    if (!this.standOn || !motionOk()) return;
    screenTint.beat(performance.now());
  }

  /**
   * Kill rewards (F4): coins arc from a screen point (the kill, projected by the level) into the points pill, each
   * landing pops the pill. Pooled and throttled in fly.ts — safe to call for every kill. Cross-file hook: called from
   * level/announcer.ts on `EnemyKilled`.
   */
  flyCoins(screenX: number, screenY: number, n: number): void {
    // A boss's coin fountain (units' coinsTo, 6+) must not be eaten by the kill-stream throttle.
    flyCoinsTo(screenX, screenY, this.els.pointsPill, n, undefined, n >= HudPills.BigCoinBurst);
  }

  /** The nodes `refresh` writes, looked up once: it runs every frame of a battle. */
  private get els(): HudNodes {
    if (this.nodes) return this.nodes;
    const q = <T extends HTMLElement>(sel: string): T => this.root.querySelector<T>(sel)!;
    return (this.nodes = { sub: q(".sub"), next: q(".next .pill > span"), stage: q(".stage"), segs: q(".segs"), pointsPill: q(".points .pill"), pointsCoin: q(".points .ic.coinic"), pointsGlint: q(".points .glint") });
  }
  private nodes: HudNodes | null = null;
  /** What the HUD last showed, so an unchanged frame compares numbers instead of building and diffing strings. */
  private shown = { rerolls: -1 };
  private nextCard: NextCardView | null = null;

  /**
   * `points` is what the points pill shows: the sim's, less the kill rewards whose rounds are still in the air
   * (level/impactWaits.ts PendingRewards), so the pill rolls up as the round lands.
   */
  refresh(sim: LevelSim, points = sim.points): void {
    const el = this.els, was = this.shown;
    if (was.rerolls !== sim.rerollCharges) { was.rerolls = sim.rerollCharges; setText(el.sub, `Power ${this.powerScore} · Rerolls ${sim.rerollCharges}`); }
    this.refreshPoints(points);
    (this.nextCard ??= new NextCardView(el.next, this.root.querySelector<HTMLElement>(".next .ring")!)).refresh(sim, points);
    this.refreshHp(sim.baseHp, sim.maxBaseHp);
    const key = `${sim.stage}|${sim.stagesCleared}|${sim.stages}`;
    if (key !== this.lastKey) {
      this.lastKey = key;
      setText(el.stage, stageShort(sim));
      el.segs.innerHTML = stageBar(sim).map((c) => `<i class="${c.state}${c.boss ? " boss" : ""}${c.groupStart ? " gap" : ""}"></i>`).join("");
    }
  }

  /**
   * Points roll up (450 ms) with a green +n and, paced by RewardPulse, the coin's pop and glint; spending snaps down — a
   * falling counter reads as a loss. This is the kill reward's only animation (no coin over the body, none flying).
   */
  private refreshPoints(points: number): void {
    const span = this.root.querySelector<HTMLElement>(".points .pill > span")!;
    const prev = this.shownPoints;
    if (points === prev) return;
    this.shownPoints = points;
    if (Number.isNaN(prev) || points < prev) { tickNumber(span, points, points, 0); return; }
    const pill = this.root.querySelector<HTMLElement>(".points .pill")!;
    // A gain landing mid-roll continues from the digits on screen, not from the last target (no jump).
    const onScreen = Number((span.textContent ?? "").replace(/,/g, ""));
    tickNumber(span, Number.isFinite(onScreen) ? Math.min(onScreen, prev) : prev, points, 450);
    plusFloater(pill, points - prev);
    if (this.pulse.gain(performance.now())) this.pulseCoin(pill);
  }

  /** The reward pulse: the pill bumps, the coin pops and tilts back, a glint star flashes on it. Reduced effects: none. */
  private pulseCoin(pill: HTMLElement): void {
    if (!motionOk()) return;
    const { pointsCoin: coin, pointsGlint: glint } = this.els, ms = HudPills.PulseMs, pop = HudPills.CoinPop, tilt = HudPills.CoinTilt;
    bump(pill, HudPills.PillPop, ms * 0.6);
    if (typeof coin.animate !== "function") return;
    // the individual scale/rotate properties, so nothing else's transform on these nodes is overwritten
    coin.animate([{ scale: "1", rotate: "0deg" }, { scale: String(pop), rotate: `${-tilt}deg`, offset: 0.3 }, { scale: "0.94", rotate: `${tilt * 0.3}deg`, offset: 0.65 }, { scale: "1", rotate: "0deg" }], { duration: ms, easing: "ease-out" });
    glint.animate([{ opacity: 0, scale: "0.2", rotate: "0deg" }, { opacity: 1, scale: "1.15", rotate: "45deg", offset: 0.35 }, { opacity: 0, scale: "0.4", rotate: "90deg" }], { duration: ms * 1.1, easing: "ease-out" });
  }

  /**
   * HP (F4/F10): the fill moves at once; a white chip behind it follows 150 ms later over 400 ms, so a hit leaves a
   * visible "this much just came off" sliver that drains away. A hit also flashes a red glow around the bar (200 ms,
   * opacity only). Both bars move by translateX inside the clipped track rather than by width — no layout, and the
   * rounded right end keeps its shape at any value. The heart pulses at ≤ 25 %.
   */
  private refreshHp(hp: number, max: number): void {
    if (hp === this.shownHp) return;
    const prev = this.shownHp;
    this.shownHp = hp;
    const frac = max > 0 ? Math.max(0, Math.min(1, hp / max)) : 0;
    const bar = this.root.querySelector<HTMLElement>(".bar.hp")!;
    const fill = bar.querySelector<HTMLElement>(".fill")!;
    const chip = bar.querySelector<HTMLElement>(".chip")!;
    const shift = `translateX(${((frac - 1) * 100).toFixed(2)}%)`;
    fill.style.transform = shift;
    // Healing: the chip jumps with the fill (it only ever trails a loss).
    chip.classList.toggle("instant", Number.isNaN(prev) || hp > prev);
    chip.style.transform = shift;
    fill.classList.toggle("red", frac <= HudPills.LowHp);
    this.root.querySelector(".heart")!.classList.toggle("low", frac <= HudPills.LowHp && hp > 0);
    setText(bar.querySelector(".hpnum"), `${hp}/${max}`);
    if (!Number.isNaN(prev) && hp < prev && motionOk()) {
      this.root.querySelector<HTMLElement>(".hpwrap .flash")!.animate([{ opacity: 0 }, { opacity: 1, offset: 0.25 }, { opacity: 0 }], { duration: 200, easing: "ease-out" });
      bump(this.root.querySelector(".heart"), 1.3, 200);
    }
  }

  dispose(): void { screenTint.reset(); }
}

interface HudNodes { sub: HTMLElement; next: HTMLElement; stage: HTMLElement; segs: HTMLElement; pointsPill: HTMLElement; pointsCoin: HTMLElement; pointsGlint: HTMLElement }

/** Writes text only when it changed, so unchanged nodes are never re-laid out. */
export function setText(el: Element | null, text: string): void {
  if (el && el.textContent !== text) el.textContent = text;
}
